import { FC, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { BuildingsIcon, CheckIcon, DatabaseIcon, XIcon } from '@phosphor-icons/react';

import { CreateTenantDto, useProvisionTenant } from '../api/admin-api';

import { adminButtonSx, adminColors } from './adminTheme';

const SECTORS = [
  'Financial Services',
  'Banking',
  'Insurance',
  'Healthcare',
  'Education',
  'Government',
  'Technology',
  'Retail',
  'Transport & Logistics',
  'Energy & Utilities',
  'Telecom',
  'Other',
];

const SLUG_RE = /^[a-z0-9-]+$/;
const slugify = (v: string) =>
  v
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Modal that provisions a new tenant. Maps design fields → CreateTenantDto. */
export const ProvisionTenantModal: FC<Props> = observer(({ open, onClose }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const provision = useProvisionTenant();

  const [displayName, setDisplayName] = useState('');
  const [name, setName] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [sector, setSector] = useState(SECTORS[0]);
  const [location, setLocation] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setDisplayName('');
    setName('');
    setSlugTouched(false);
    setContactEmail('');
    setSector(SECTORS[0]);
    setLocation('');
    setSubmitted(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const nameError = submitted && (!name || !SLUG_RE.test(name));
  const displayNameError = submitted && !displayName.trim();
  const emailError = submitted && !contactEmail.trim();

  const handleSubmit = () => {
    setSubmitted(true);
    if (!displayName.trim() || !contactEmail.trim() || !name || !SLUG_RE.test(name)) return;
    const body: CreateTenantDto = {
      name,
      displayName: displayName.trim(),
      contactEmail: contactEmail.trim(),
      ...(sector ? { sector } : {}),
      ...(location.trim() ? { location: location.trim() } : {}),
    };
    provision.mutate(body, { onSuccess: close });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 5, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.625,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2.5,
            backgroundColor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BuildingsIcon size={20} color={c.orange} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: c.ink }}>
            {t('app.admin.provision.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: c.faint, mt: 0.125 }}>
            {t('app.admin.provision.subtitle')}
          </Typography>
        </Box>
        <Button
          onClick={close}
          sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: 2, backgroundColor: theme.palette.grey[200], color: c.sub }}
        >
          <XIcon size={16} />
        </Button>
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label={t('app.admin.provision.displayName')}
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            if (!slugTouched) setName(slugify(e.target.value));
          }}
          placeholder={t('app.admin.provision.displayNamePlaceholder')}
          error={displayNameError}
          helperText={displayNameError ? t('app.admin.provision.validation.required') : undefined}
          size="small"
          fullWidth
        />
        <TextField
          label={t('app.admin.provision.slug')}
          value={name}
          onChange={(e) => {
            setName(slugify(e.target.value));
            setSlugTouched(true);
          }}
          placeholder={t('app.admin.provision.slugPlaceholder')}
          error={nameError}
          helperText={nameError ? t('app.admin.provision.validation.slug') : t('app.admin.provision.slugHint')}
          size="small"
          fullWidth
          InputProps={{
            endAdornment: <InputAdornment position="end">.komplyance.sa</InputAdornment>,
          }}
        />
        <TextField
          label={t('app.admin.provision.email')}
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          placeholder={t('app.admin.provision.emailPlaceholder')}
          error={emailError}
          helperText={emailError ? t('app.admin.provision.validation.required') : undefined}
          size="small"
          fullWidth
        />
        <TextField
          select
          label={t('app.admin.provision.sector')}
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          size="small"
          fullWidth
        >
          {SECTORS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('app.admin.provision.location')}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={t('app.admin.provision.locationPlaceholder')}
          size="small"
          fullWidth
        />

        <Box
          sx={{
            display: 'flex',
            gap: 1.125,
            backgroundColor: theme.palette.grey[200],
            borderRadius: 2.5,
            p: 1.75,
          }}
        >
          <DatabaseIcon size={17} color={c.sub} style={{ marginTop: 1, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11.5, color: c.sub, lineHeight: 1.55 }}>
            {t('app.admin.provision.note')}
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.25,
          borderTop: `1px solid ${c.border}`,
        }}
      >
        <Button
          onClick={close}
          sx={{ ...adminButtonSx(theme).neutral, px: 2, borderRadius: 2.75 }}
        >
          {t('app.admin.provision.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={provision.isPending}
          startIcon={<CheckIcon size={16} />}
          sx={{ ...adminButtonSx(theme).solid, px: 2, borderRadius: 2.75 }}
        >
          {t('app.admin.provision.submit')}
        </Button>
      </Box>
    </Dialog>
  );
});
